import { useStore } from "ra-core";
import { useCallback, useSyncExternalStore } from "react";

/** The two desktop home views: A «Hoy» (default) and B «Pulso». */
export const HOME_VIEWS = ["today", "pulse"] as const;
export type HomeView = (typeof HOME_VIEWS)[number];

/**
 * react-admin store key of the per-user default view (localStorage-backed
 * RaStore, set in Settings). Not a DB column: see proposed migrations.
 */
export const HOME_DEFAULT_VIEW_STORE_KEY = "home.defaultView";

export const isHomeView = (value: unknown): value is HomeView =>
  HOME_VIEWS.includes(value as HomeView);

/**
 * The view picked with the header toggle during this page session. Kept in
 * memory on purpose: it survives navigating away and back, but a reload
 * starts again from the default chosen in Settings.
 */
let sessionView: HomeView | null = null;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSessionView = () => sessionView;

const setSessionView = (view: HomeView | null) => {
  sessionView = view;
  listeners.forEach((listener) => listener());
};

/** Tests: forget the session choice between cases. */
export const resetHomeViewSession = () => setSessionView(null);

/** Default view from Settings («Hoy» unless the user picked «Pulso»). */
export const useHomeDefaultView = (): [HomeView, (view: HomeView) => void] => {
  const [stored, setStored] = useStore<HomeView>(
    HOME_DEFAULT_VIEW_STORE_KEY,
    "today",
  );
  return [isHomeView(stored) ? stored : "today", setStored];
};

/** Current desktop home view: this session's pick, else the default. */
export const useHomeView = (): [HomeView, (view: HomeView) => void] => {
  const [defaultView] = useHomeDefaultView();
  const session = useSyncExternalStore(subscribe, getSessionView);
  const setView = useCallback((view: HomeView) => setSessionView(view), []);
  return [session ?? defaultView, setView];
};
